import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { queryClient } from '@/lib/queryClient';
import { router } from '@/router';
import { ErrorBoundary } from '@/components/ui';
import { registerToastHandler, showToast, type ToastVariant } from '@/lib/toast';
import { getErrorMessage } from '@/lib/errors';
import '@/index.css';

// Apply persisted theme before first paint (avoids a flash of the wrong theme)
(function () {
  try {
    const stored = localStorage.getItem('aidr-theme');
    if (stored) {
      const { state } = JSON.parse(stored) as { state?: { theme?: string } };
      if (state?.theme === 'light') {
        document.documentElement.setAttribute('data-theme', 'light');
      }
    }
  } catch {
    // ignore parse errors
  }
})();

// ── Toast notifications — the confirmation feedback shown after CRUD actions ──
// Lightweight DOM-based queue, no extra dependency needed for something this small.

function ToastProvider() {
  useEffect(() => {
    const container = document.createElement('div');
    container.id = 'toast-root';
    container.style.cssText =
      'position:fixed;bottom:1rem;right:1rem;z-index:9999;display:flex;flex-direction:column;gap:0.5rem;pointer-events:none';
    document.body.appendChild(container);

    const handler = (message: string, variant: ToastVariant) => {
      const el = document.createElement('div');
      el.textContent = message;
      const borderColor = variant === 'error' ? 'var(--color-state-danger,#f38ba8)' : 'var(--color-state-success,#10B981)';
      el.style.cssText = `background:var(--color-surface-2,#1e1e2e);color:var(--color-text-primary,#e2e2e9);border:1px solid ${borderColor};border-radius:0.375rem;padding:0.5rem 0.875rem;font-size:0.75rem;opacity:0;transition:opacity 0.15s;pointer-events:auto;max-width:20rem;word-break:break-word`;
      container.appendChild(el);
      requestAnimationFrame(() => { el.style.opacity = '1'; });
      setTimeout(() => {
        el.style.opacity = '0';
        setTimeout(() => container.removeChild(el), 160);
      }, 4000);
    };

    registerToastHandler(handler);

    return () => {
      registerToastHandler(null);
      document.body.removeChild(container);
    };
  }, []);

  return null;
}

// Any mutation that fails surfaces here as a toast — unless it opts out via
// meta: { suppressErrorToast: true }, for mutations that render their own
// inline error (e.g. a form in a Drawer) instead of a floating toast.
queryClient.getMutationCache().config.onError = (error, _variables, _context, mutation) => {
  if (mutation.options.meta?.suppressErrorToast) return;
  showToast(getErrorMessage(error, 'An unexpected error occurred.'), 'error');
};

const root = document.getElementById('root');
if (!root) throw new Error('Root element #root not found');

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider />
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
